import * as Sentry from '@sentry/angular';
import { EEnvTypes } from '@common/enums/env-types.enum';
import type { EduUser, LandingPageUser } from '@common/types/user.type';
import { ErrorHandler } from '@angular/core';

export function initBrowserSentry({
    dsn,
    environment,
    project,
}: {
    dsn: string;
    environment: EEnvTypes;
    project: 'ml' | 'websites' | 'websites-builder';
}) {
    if (environment === EEnvTypes.DEV) {
        // experimental
        Sentry.init({
            sampleRate: 1.0,
            tracesSampleRate: 1.0,
            enableLogs: true,
            integrations: [
                Sentry.zodErrorsIntegration({ key: 'zod', limit: 20, saveZodIssuesAsAttachment: false }),
                Sentry.spotlightBrowserIntegration(),
                Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
                Sentry.browserTracingIntegration(),
            ],
        });
    } else {
        // all non-dev environments including production
        const integrations: Sentry.BrowserOptions['integrations'] = [
            Sentry.consoleLoggingIntegration({ levels: ['error'] }),
        ];

        if (project === 'websites-builder') {
            integrations.push(
                Sentry.browserTracingIntegration({
                    beforeStartSpan(span) {
                        if (span.op === 'pageload' || span.op === 'navigation') {
                            let name = span.name;
                            if (span.name.startsWith('/')) {
                                name = '/:page';
                            } else if (span.name === 'blank') {
                                name = 'puppeteer';
                            } else if (span.name === 'srcdoc') {
                                name = 'preview';
                            }
                            return {
                                ...span,
                                name,
                                attributes: { ...span.attributes, originalSpanName: span.name },
                            };
                        }
                        return span;
                    },
                })
            );
        }

        const config: Sentry.BrowserOptions = {
            dsn,
            environment,
            sendDefaultPii: true,
            // release: environment.sentryRelease,
            integrations,
            tracesSampler: (samplingContext) => {
                if (samplingContext.parentSampled !== undefined) {
                    return samplingContext.parentSampled;
                }

                if (project !== 'websites-builder') {
                    return 0.0;
                }

                switch (environment) {
                    case EEnvTypes.PROD:
                        return 0.05;
                    case EEnvTypes.PRE:
                        return 0.5;
                    case EEnvTypes.STAGE:
                        return 1.0;
                }
            },

            tracePropagationTargets: [/^\/api/],
            enableLogs: true,

            normalizeDepth: 10,
        };

        Sentry.init(config);
    }
}
export function setUserContext(user: LandingPageUser): void {
    Sentry.setUser({
        id: user.user_id,
        email: user.user_email,
        sendpulse_vpn: !!user.is_office,
    });

    Sentry.setContext('tariff', {
        abbr: `${user.tariff.abbr} (${user.tariff.version})`,
        start_date: user.start_date,
        end_date: user.end_date,
        autorenew: Boolean(user.autorenew_tariff),
    });
}

export function setEduUserContext(user: EduUser): void {
    Sentry.setContext('eduUser', {
        user_status: user.user.status,
        abbr: user.tariff.abbr,
        start_date: user.start_date,
        end_date: user.end_date,
        autorenew: Boolean(user.autorenew_tariff),
    });
}

export const SENTRY_ERROR_HANDLER_PROVIDER = {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
        showDialog: false,
    }),
};
